import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import TextReviewIcon from '@atlaskit/icon-lab/core/text-review';
import { usePublish } from '@atlaskit/rovo-triggers/main';

import { useAISmartButtonActions } from '@atlassian/ai-smart-button-api/ai-smart-button-store';
import {
	type ConversationAction,
	type SkillAction,
	HandleActionType,
	usePromptCards,
} from '@atlassian/rovo-conversation-actions';

import { applyOnboardingRovoConfig } from '@confluence/experiment-rovo-onboarding-twcg-1387/entry-points/applyOnboardingRovoConfig.ts';
import { useOnboardingEntryPointPublish } from '@confluence/experiment-rovo-onboarding-twcg-1387/entry-points/useOnboardingEntryPointPublish.ts';
import { useAudioButtonWrapperStore } from '@confluence/audio/entry-points/useAudioButtonWrapperStore.tsx';
import { CommentCountsProvider } from '@confluence/comment-context/entry-points/CommentCountsContext.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { useNudgeContributorFilter } from '@confluence/page-rovo-config-common/entry-points/useNudgeContributorFilter.ts';
import { useNudgeFirstVisitFilter } from '@confluence/page-rovo-config-common/entry-points/useNudgeFirstVisitFilter.ts';
import { TASK_ITEM_NODE_TYPE } from '@confluence/page-rovo-config-common/entry-points/useAgentMentionNudgeAction.ts';
import { useViewPageRovoActions } from '@confluence/page-rovo-config-common/entry-points/useViewPageRovoActions.ts';
import {
	type RovoConfiguration,
	type RovoConfigurationProviderProps,
	type PageRovoConfigProviderProps,
	isPageRovoConfigProviderProps,
} from '@confluence/rovo-config-provider-common/entry-points/rovoConfigTypes.ts';
import { useRovoConfigProviderProps } from '@confluence/rovo-config-provider-common/entry-points/useRovoConfigProviderProps.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { useLiveDocNudgeRecommendationFilter } from './hooks/useLiveDocNudgeRecommendationFilter.ts';
import { useEditPageNudgeConfig } from './hooks/useEditPageNudgeConfig.ts';
import {
	useEditorRovoButtonActionsActions,
	useEditorRovoButtonActionsContext,
	useEditorRovoButtonAgentMentionId,
	useEditorRovoButtonAgentMentionInsertionCount,
	useEditorRovoButtonResolvedAgentMentionId,
	useEditorRovoButtonAgentMentionContext,
	useEditorRovoButtonAgentMentionPrompt,
	useEditorRovoButtonAgentMentionName,
	useEditorRovoButtonAgentMentionParentNodeType,
} from './store/EditorRovoButtonActionsStore.tsx';
import { getEmptyPageCreationActions } from './utils/getEmptyPageCreationActions.tsx';
import emptyPageGreetingIcon from './assets/empty-page-greeting-icon.svg';

const messages = defineMessages({
	emptyPageGreetingTitle: {
		id: 'edit-page-rovo-config-provider.empty-page-greeting-title',
		defaultMessage: 'Start your doc with a prompt',
		description: 'Greeting title shown in the Rovo empty state when the editor page is empty',
	},
	copiedPageGreetingTitle: {
		id: 'edit-page-rovo-config-provider.copied-page-greeting-title',
		defaultMessage: 'Need help editing your copied page?',
		description:
			'Greeting title shown in the Rovo chat sidebar when the user is editing a page that was recently copied',
	},
	suggestEditsMenuItemLabel: {
		id: 'edit-page-rovo-config-provider.menu.item.label.suggest-edits',
		defaultMessage: 'Review with suggestions',
		description:
			'Label for the Suggest Edits skill action in the Rovo Chat actions list while editing a page. Clicking this sends a prompt to Rovo to review the page content with the suggest-edits skill.',
	},
	suggestEditsMenuItemPrompt: {
		id: 'edit-page-rovo-config-provider.menu.item.prompt.suggest-edits',
		defaultMessage: 'Review my content with the /suggest-edits skill',
		description:
			'Prompt sent to Rovo Chat when a user clicks the Refine action while editing a page. Rovo will review the page content using the suggest-edits skill.',
	},
});

const EditPageConfigProviderInner = ({ setConfiguration }: RovoConfigurationProviderProps) => {
	// Temporary: opens Rovo mini-modal for Founding Admins landing after onboarding.
	// Remove when experiment twcg-1387-rovo-in-onboarding-chat is cleaned up.
	const onboardingConfig = useOnboardingEntryPointPublish();

	const viewPageRovoActions = useViewPageRovoActions();
	const editorActions = useEditorRovoButtonActionsActions();
	const editorActionsContext = useEditorRovoButtonActionsContext();
	const rovoConfigProviderProps = useRovoConfigProviderProps();
	const { isRovoLLMEnabled } = useSessionData();
	const isEditorEmpty =
		isPageRovoConfigProviderProps(rovoConfigProviderProps) && rovoConfigProviderProps.isEditorEmpty;
	const { formatMessage } = useIntl();

	const { promptCards } = usePromptCards();

	const isEmptyPageWithPromptCards = isEditorEmpty && Array.isArray(promptCards);

	const pageProps = isPageRovoConfigProviderProps(rovoConfigProviderProps)
		? rovoConfigProviderProps
		: null;

	const contentType = pageProps?.contentType ?? '';
	const contentId = pageProps?.contentId ?? '';
	const readTimeMinutes = pageProps?.readTime ?? null;
	const readTimeLoading = pageProps?.readTimeLoading ?? false;
	const suggestEditsActionIcon = useCallback(() => <TextReviewIcon label="" />, []);

	const enableLiveDocNudges = useLiveDocNudgeRecommendationFilter(contentType);

	const { eligibleForChangeboarding } = useAudioButtonWrapperStore();
	const { isFirstVisit, loading: isFirstVisitLoading } = useNudgeFirstVisitFilter(
		contentId,
		!enableLiveDocNudges,
	);
	const { isCurrentUserContributor, loading: isContributorLoading } = useNudgeContributorFilter(
		contentId,
		!enableLiveDocNudges,
	);

	const isEligibilityLoading = isFirstVisitLoading || isContributorLoading || readTimeLoading;

	const agentMentionId = useEditorRovoButtonAgentMentionId();
	const agentMentionInsertionCount = useEditorRovoButtonAgentMentionInsertionCount();
	const resolvedAgentMentionId = useEditorRovoButtonResolvedAgentMentionId();
	const agentMentionContext = useEditorRovoButtonAgentMentionContext();
	const agentMentionPrompt = useEditorRovoButtonAgentMentionPrompt();
	const agentMentionName = useEditorRovoButtonAgentMentionName();
	const agentMentionParentNodeType = useEditorRovoButtonAgentMentionParentNodeType();
	const isTaskItemMention = agentMentionParentNodeType === TASK_ITEM_NODE_TYPE;

	const nudges = useEditPageNudgeConfig(
		enableLiveDocNudges,
		eligibleForChangeboarding,
		isFirstVisit,
		isCurrentUserContributor,
		isEligibilityLoading,
		readTimeMinutes,
		// Task items use auto-fire instead; expValEquals last ensures exposure is tracked.
		!isTaskItemMention && expValEquals('platform_editor_agent_mentions', 'isEnabled', true)
			? agentMentionId
			: null,
		resolvedAgentMentionId,
		agentMentionContext,
		agentMentionPrompt,
		agentMentionParentNodeType,
		agentMentionInsertionCount,
		agentMentionName,
	);
	const { setNudges } = useAISmartButtonActions();
	const publish = usePublish('ai-mate');

	// Keep refs so the auto-fire effect can read the latest agent mention prompt/context without
	// them being reactive dependencies. Adding them to the dep array would cause the effect to
	// re-fire on every keystroke in the task item without agentMentionInsertionCount changing.
	const agentMentionPromptRef = useRef(agentMentionPrompt);
	agentMentionPromptRef.current = agentMentionPrompt;
	const agentMentionContextRef = useRef(agentMentionContext);
	agentMentionContextRef.current = agentMentionContext;
	// Auto-fire Rovo chat when an agent is @mentioned inside a task item.
	// Task item mentions are action-oriented, so open the chat immediately with
	// the agent pre-selected and the task text as the context prompt.
	// Gated behind the platform_editor_agent_mentions experiment.
	useEffect(() => {
		if (
			!isTaskItemMention ||
			!resolvedAgentMentionId ||
			!agentMentionInsertionCount ||
			!expValEquals('platform_editor_agent_mentions', 'isEnabled', true)
		) {
			return;
		}

		const prompt = agentMentionContextRef.current ?? agentMentionPromptRef.current;

		publish({
			type: 'chat-new',
			source: 'agentMentionTaskItemAutoFire',
			openChatMode: 'mini-modal',
			data: {
				...(prompt ? { prompt } : {}),
				isPromptPlaceholder: false,
				dialogues: [],
				mode: { useCurrentPageContext: true },
				agentId: resolvedAgentMentionId,
			},
		});
	}, [
		agentMentionInsertionCount,
		resolvedAgentMentionId,
		isTaskItemMention,
		// agentMentionPrompt/agentMentionContext intentionally omitted — read via refs above to avoid
		// re-firing on every keystroke after the mention is inserted.
		publish,
	]);

	const config = useMemo<RovoConfiguration>(() => {
		const getCustomGreeting = () => {
			if (isEmptyPageWithPromptCards) {
				return {
					iconSrc: emptyPageGreetingIcon,
					title: formatMessage(messages.emptyPageGreetingTitle),
				};
			}
			if (expValEquals('cc_fd_copy_with_rovo', 'cohort', 'rovoChat')) {
				return { title: formatMessage(messages.copiedPageGreetingTitle) };
			}
			return undefined;
		};

		const customGreeting = getCustomGreeting();

		const suggestEditsAction: SkillAction = {
			id: 'suggest-edits-ai-action',
			title: formatMessage(messages.suggestEditsMenuItemLabel),
			icon: suggestEditsActionIcon,
			handleType: HandleActionType.Skill,
			type: 'skill-action',
			staticPrompt: formatMessage(messages.suggestEditsMenuItemPrompt),
		};

		/** Optionally inject suggest-edits skill action */
		const combinedEditorActions =
			isRovoLLMEnabled &&
			expValEquals('platform_editor_ai_content_suggested_edits', 'isEnabled', true)
				? [suggestEditsAction, ...editorActions]
				: editorActions;

		const baseConfig: RovoConfiguration = {
			getActions: async () => {
				if (isEmptyPageWithPromptCards) {
					return Promise.resolve(getEmptyPageCreationActions(promptCards));
				}

				if (editorActionsContext?.reference) {
					return Promise.resolve(combinedEditorActions);
				}

				const viewPageActions = viewPageRovoActions.map(
					(action: { action: ConversationAction }) => action.action,
				);

				return Promise.resolve([...combinedEditorActions, ...viewPageActions]);
			},
			customGreeting,
			shouldShowGreeting: customGreeting !== undefined || undefined,
			prioritizeCustomActionResults: isEmptyPageWithPromptCards ? true : undefined,
		};
		return applyOnboardingRovoConfig(baseConfig, onboardingConfig);
	}, [
		editorActions,
		editorActionsContext,
		viewPageRovoActions,
		isEmptyPageWithPromptCards,
		isRovoLLMEnabled,
		promptCards,
		formatMessage,
		suggestEditsActionIcon,
		onboardingConfig,
	]);

	useEffect(() => {
		setConfiguration(config);
		setNudges(nudges);
	}, [config, setConfiguration, nudges, setNudges]);

	return null;
};

export const EditPageRovoConfigProvider = (
	props: RovoConfigurationProviderProps,
): React.JSX.Element => {
	const { contentId } = useRovoConfigProviderProps() as PageRovoConfigProviderProps;
	return (
		<CommentCountsProvider contentId={contentId}>
			<EditPageConfigProviderInner {...props} />
		</CommentCountsProvider>
	);
};
